import styled from '@emotion/styled';
import React, { ReactNode } from 'react';
import { useTheme } from 'emotion-theming';
import { Box, Card, Flex } from '@qga/roo-ui/components';
import { themeGet } from 'styled-system';
import Image from 'components/Image';
import PromotionalSashNew from 'components/PromotionalSashNew';
import AppLink from 'components/AppLink';
import { useSelector } from 'react-redux';
import { getPageName } from 'store/router/routerSelectors';
import { getCampaignDefaultSash } from 'store/campaign/campaignSelectors';

const BaseCard = styled(Card)`
  width: 100%;
  background-color: #fff;
  padding: 0;
  transition: box-shadow 0.2s;
  position: relative;
  box-shadow: ${themeGet('shadows.default')};

  &:hover {
    box-shadow: ${themeGet('shadows.hover')};
  }
`;

const BaseBadge = styled(Box)`
  width: ${themeGet('space.5')};
`;

const InlineCardWrapper = styled(BaseCard)`
  ${themeGet('card.inline.root')};
`;

const InlineBadge = styled(Box)`
  ${themeGet('card.inline.badge')}
`;

const InlineText = styled(Flex)`
  ${themeGet('card.inline.text')}
  padding-top: 0
`;

const StackedCardWrapper = styled(BaseCard)`
  ${themeGet('card.stacked.root')};
`;

const StackedBadge = styled(Box)`
  ${themeGet('card.stacked.badge')}
`;

const StackedImage = styled(Image)`
  ${themeGet('card.stacked.image')}
`;

const ImageWrapper = styled(Box)`
  overflow: hidden;
  width: 100%;
  height: 100%;
  border-top-right-radius: ${themeGet('radii.default')};
  border-top-left-radius: ${themeGet('radii.default')};
`;

const NakedAppLink = styled(AppLink)`
  display: block;
  text-decoration: none;
`;

type Props = {
  compact?: boolean;
  children: ReactNode;
  imageAltTag: string;
  imageSrc: string;
  imageSrcSet?: string;
  onClick?: () => void;
  promotionName?: string;
  target?: string;
  to: string;
  isClassic: boolean;
  isPromoCard: boolean;
};

export const InlineCard = ({
  compact,
  children,
  imageSrc,
  imageSrcSet,
  imageAltTag,
  promotionName,
  isClassic,
  isPromoCard,
  ...rest
}: Props) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const theme: any = useTheme();
  const pageName = useSelector(getPageName);
  const isDealsPage = pageName === 'deals';

  const imageSize =
    compact && isDealsPage
      ? { ...theme.imageSizes.mapSearchResultPhoneDealPage }
      : compact
        ? { ...theme.imageSizes.mapSearchResultPhone }
        : { ...theme.imageSizes.mapSearchResult };

  const boxWidth =
    compact && isDealsPage
      ? { ...theme.boxWidths.dealsBoxWidth }.width
      : compact
        ? { ...theme.boxWidths.searchBoxWidthPhone }.width
        : { ...theme.boxWidths.searchBoxWidth }.width;

  const globalCampaignDefaultSash = useSelector(getCampaignDefaultSash);
  const useGlobalCampaignDefaultSash = globalCampaignDefaultSash && !isClassic && !isDealsPage && !isPromoCard;
  const promotionSash = useGlobalCampaignDefaultSash ? globalCampaignDefaultSash : promotionName;

  return (
    <InlineCardWrapper as={NakedAppLink} rel="noopener noreferrer" {...rest} data-testid="inline-card">
      <Flex flexDirection="row" height="100%" minHeight="235px">
        <Box width={boxWidth}>
          <Image src={imageSrc} srcSet={imageSrcSet} alt={imageAltTag} {...imageSize} />
        </Box>

        <InlineText flexDirection="column" justifyContent="space-between" width={1}>
          {promotionSash && !isPromoCard && (
            <InlineBadge>
              <PromotionalSashNew promotionName={promotionSash} />
            </InlineBadge>
          )}
          {promotionSash && isPromoCard && (
            <BaseBadge>
              <PromotionalSashNew promotionName={promotionSash} />
            </BaseBadge>
          )}
          {children}
        </InlineText>
      </Flex>
    </InlineCardWrapper>
  );
};

export const StackedCard = ({ children, imageSrc, imageSrcSet, imageAltTag, promotionName, isClassic, ...rest }: Props) => {
  const globalCampaignDefaultSash = useSelector(getCampaignDefaultSash);
  const useGlobalCampaignDefaultSash = globalCampaignDefaultSash && !isClassic;
  const promotionSash = useGlobalCampaignDefaultSash ? globalCampaignDefaultSash : promotionName;

  return (
    <StackedCardWrapper as={NakedAppLink} rel="noopener noreferrer" width={300} {...rest} data-testid="stacked-card">
      <Flex flexDirection="column" justifyContent="space-between" height="100%">
        <ImageWrapper>
          {promotionSash && (
            <StackedBadge position="absolute">
              <PromotionalSashNew promotionName={promotionSash} type="corner" />
            </StackedBadge>
          )}
          <Box>
            <StackedImage lazy src={imageSrc} srcSet={imageSrcSet} alt={imageAltTag} height={120} width={1} />
          </Box>
        </ImageWrapper>
        <Box m={3} height="100%">
          {children}
        </Box>
      </Flex>
    </StackedCardWrapper>
  );
};
